import React, { useRef, useCallback, useEffect, useState } from 'react';
import { Box, IconButton, Tooltip, Typography, Paper, Divider } from '@mui/material';
import {
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  FormatUnderlined as UnderlineIcon,
  StrikethroughS as StrikeIcon,
  Link as LinkIcon,
} from '@mui/icons-material';

export interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  maxLength: number;
  placeholder?: string;
  minRows?: number;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  maxLength,
  placeholder = 'Enter text...',
  minRows = 3,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const isInternalChange = useRef(false);
  const [charCount, setCharCount] = useState(0);

  // Only update the editor content when value changes externally
  useEffect(() => {
    if (editorRef.current && !isInternalChange.current) {
      // Only update if the content is actually different
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value;
      }
    }
    isInternalChange.current = false;
    // Update character count
    setCharCount(value.replace(/<[^>]*>/g, '').length);
  }, [value]);

  const execCommand = useCallback((command: string, val?: string) => {
    document.execCommand(command, false, val);
    // Trigger onChange with the new content
    if (editorRef.current) {
      isInternalChange.current = true;
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const handleFormat = (command: string) => () => {
    execCommand(command);
    editorRef.current?.focus();
  };

  const handleInput = () => {
    if (editorRef.current) {
      isInternalChange.current = true;
      onChange(editorRef.current.innerHTML);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    // Only paste plain text to avoid pasting formatted content
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  const isOverLimit = charCount > maxLength;

  return (
    <Box sx={{ width: '100%' }}>
      <Paper variant="outlined" sx={{ borderRadius: 1 }}>
        {/* Toolbar */}
        <Box sx={{ display: 'flex', gap: 0.5, p: 0.5, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
          <Tooltip title="Bold (Ctrl+B)">
            <IconButton size="small" onClick={handleFormat('bold')}>
              <BoldIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Italic (Ctrl+I)">
            <IconButton size="small" onClick={handleFormat('italic')}>
              <ItalicIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Underline (Ctrl+U)">
            <IconButton size="small" onClick={handleFormat('underline')}>
              <UnderlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Strikethrough">
            <IconButton size="small" onClick={handleFormat('strikeThrough')}>
              <StrikeIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <Tooltip title="Add Link">
            <IconButton
              size="small"
              onClick={() => {
                const url = window.prompt('Enter URL:');
                if (url) {
                  execCommand('createLink', url);
                }
              }}
            >
              <LinkIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Editor Area */}
        <Box
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onPaste={handlePaste}
          dir="ltr"
          sx={{
            p: 1.5,
            minHeight: `${minRows * 1.5}em`,
            outline: 'none',
            textAlign: 'left',
            direction: 'ltr',
            unicodeBidi: 'plaintext',
            '&:empty:before': {
              content: `"${placeholder}"`,
              color: 'text.disabled',
            },
            '& a': {
              color: 'primary.main',
              textDecoration: 'underline',
            },
          }}
        />
      </Paper>

      {/* Character Count */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
        <Typography
          variant="caption"
          color={isOverLimit ? 'error' : charCount > maxLength * 0.9 ? 'warning.main' : 'text.secondary'}
        >
          {charCount}/{maxLength}
        </Typography>
      </Box>
    </Box>
  );
};
